import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Plans = () => {
  const navigate = useNavigate();
  const [billingType, setBillingType] = useState('monthly');

  const handleBillingChange = (event, newBillingType) => {
    if (newBillingType !== null) {
      setBillingType(newBillingType);
    }
  };

  const handleButtonClick = (planName) => {
    if (planName === 'Free Trial Plan') {
      navigate('/signup');
    } else if (planName === 'Basic Plan' || planName === 'Premium Plan') {
      navigate('/contact');
    }
  };

  const plans = [
    {
      name: 'Free Trial Plan',
      price: '$0',
      subtitle: 'Experience Full Access for 7 Days',
      features: [
        'Basic Data Extraction',
        'Standard Transformation Tools',
        'Manual Workflow Execution',
        'Data Loading to Common Systems',
        'Basic Monitoring & Error Logging',
      ],
      isHighlighted: false,
      buttonText: 'Get Started',
    },
    {
      name: 'Basic Plan',
      price: 'Custom Pricing',
      subtitle: 'Essential ETL Features Include',
      features: [
        'Data Extraction',
        'Basic Transformation',
        'Automated Scheduling',
        'Data Loading',
        'Monitoring & Logging',
      ],
      isHighlighted: true,
      buttonText: 'Contact Us',
    },
    {
      name: 'Premium Plan',
      price: 'Custom Pricing',
      subtitle: 'Advance features for Business',
      features: [
        'Advanced Data Integration',
        'Enhance Transformation Tools',
        'Real-Time Data Processing',
        'Custom Workflow Automation',
        'Comprehensive Monitoring & Alerts',
        'Scalability & Flexibility',
        'Security & Compliance',
      ],
      isHighlighted: false,
      buttonText: 'Contact Us',
    },
  ];

  return (
    <Box
      id="pricing"
      sx={{
        width: '100%',
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 4 },
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            color: '#383838',
            mb: 2,
            textAlign: 'center',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            maxWidth: '900px',
            mx: 'auto',
          }}
        >
          Flexible Plans to Elevate Your Data Management
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#0450D0',
            fontSize: { xs: '0.9rem', md: '1rem' },
            textAlign: 'center',
            mb: 4,
            fontWeight: 500,
          }}
        >
          Simple, transparent pricing that grows with you. Try any plan free for
          7 days.
        </Typography>

        {/* Billing Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <ToggleButtonGroup
            value={billingType}
            exclusive
            onChange={handleBillingChange}
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '25px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '25px !important',
                px: 3,
                py: 1,
                color: '#666',
                fontWeight: 500,
                '&.Mui-selected': {
                  backgroundColor: '#1e40af',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#1e40af',
                  },
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
            }}
          >
            <ToggleButton value="monthly">Monthly billing</ToggleButton>
            <ToggleButton value="annual">Annual billing</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </motion.div>

      {/* Pricing Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: { xs: 3, md: 4 },
          maxWidth: '1200px',
          width: '100%',
        }}
      >
        {plans.map((plan, index) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.2, ease: 'easeOut' }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                borderRadius: plan.isHighlighted ? '9.806px' : '9.021px',
                border: plan.isHighlighted ? 'none' : '0.902px solid #032774',
                background: plan.isHighlighted
                  ? 'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)'
                  : '#FFF',
                transform: plan.isHighlighted ? 'scale(1.05)' : 'scale(1)',
                boxShadow: plan.isHighlighted
                  ? '0 0 40px 0 rgba(0, 86, 210, 0.3), 0 10px 30px rgba(0, 0, 0, 0.15)'
                  : '0 4px 6px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
                zIndex: plan.isHighlighted ? 10 : 1,
                '&:hover': {
                  transform: plan.isHighlighted
                    ? 'scale(1.05) translateY(-4px)'
                    : 'translateY(-4px)',
                  boxShadow: plan.isHighlighted
                    ? '0 0 50px 0 rgba(0, 86, 210, 0.4), 0 15px 40px rgba(0, 0, 0, 0.2)'
                    : '0 8px 25px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 3, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                {/* Plan Name */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    color: plan.isHighlighted ? '#ffffff' : '#1f2937',
                    mb: 1,
                  }}
                >
                  {plan.name}
                </Typography>

                {/* Price */}
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '2.5rem',
                    color: plan.isHighlighted ? '#ffffff' : '#2563eb',
                    mb: 2,
                  }}
                >
                  {plan.price}
                </Typography>

                {/* Subtitle */}
                <Typography
                  variant="body2"
                  sx={{
                    color: plan.isHighlighted ? '#e5e7eb' : '#6b7280',
                    mb: 3,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }}
                >
                  {plan.subtitle}
                </Typography>

                {/* Features List */}
                <List sx={{ flexGrow: 1, p: 0 }}>
                  {plan.features.map((feature, featureIndex) => (
                    <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleIcon
                          sx={{
                            color: plan.isHighlighted ? '#ffffff' : '#10b981',
                            fontSize: '1.2rem',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: plan.isHighlighted ? '#ffffff' : '#374151',
                            fontSize: '0.9rem',
                            fontWeight: 400,
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Get Started Button */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleButtonClick(plan.name)}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: plan.isHighlighted ? '8px' : '7.217px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    width: '100%',
                    alignSelf: 'stretch',
                    ...(plan.isHighlighted
                      ? {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          border: 'none',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                          },
                        }
                      : {
                          background:
                            'linear-gradient(90deg, #0548C2 0%, #032A6E 100%)',
                          color: '#ffffff',
                          border: 'none',
                          '&:hover': {
                            background:
                              'linear-gradient(90deg, #0441b8 0%, #02245a 100%)',
                          },
                        }),
                  }}
                >
                  {plan.buttonText}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default Plans;
